import { useCallback, useEffect, useState } from "react";
import { Send, Power, Trash2, CheckCircle2, Bot, Headset, MessageSquare, Volume2, VolumeX } from "lucide-react";
import { SOUND_KEY, soundOn, playPling } from "../hooks/useChatUnread";
import ChatMessages from "./ChatMessages";

const API = process.env.REACT_APP_BACKEND_URL;
const POLL_MS = 4000;

export default function AdminChat({ token, onUnauthorized }) {
  const h = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
  const [online, setOnline] = useState(false);
  const [convs, setConvs] = useState([]);
  const [active, setActive] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sound, setSound] = useState(soundOn);
  const toggleSound = () => {
    const next = !sound;
    localStorage.setItem(SOUND_KEY, next ? "on" : "off");
    setSound(next);
    if (next) setTimeout(playPling, 50);
  };

  const get = useCallback(async (path) => {
    const res = await fetch(`${API}/api/admin/chat${path}`, { headers: h });
    if (res.status === 401) { onUnauthorized(); return null; }
    return res.json();
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadList = useCallback(async () => {
    const [s, list] = await Promise.all([get("/settings"), get("/conversations")]);
    if (s) setOnline(s.online);
    if (list) setConvs(list);
  }, [get]);

  const loadThread = useCallback(async (cid) => {
    const d = await get(`/conversations/${cid}/messages`);
    if (d) { setMessages(d.messages); setActive(d.conversation); }
  }, [get]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const t = setInterval(() => { loadList(); if (active) loadThread(active.id); }, POLL_MS);
    return () => clearInterval(t);
  }, [loadList, loadThread, active]);

  const toggleOnline = async () => {
    const d = await fetch(`${API}/api/admin/chat/settings`, { method: "PUT", headers: h, body: JSON.stringify({ online: !online }) }).then((r) => r.json());
    setOnline(d.online);
    loadList();
  };

  const reply = async (e) => {
    e.preventDefault();
    const text = draft.trim();
    if (!text || !active) return;
    setDraft("");
    await fetch(`${API}/api/admin/chat/conversations/${active.id}/reply`, { method: "POST", headers: h, body: JSON.stringify({ text }) });
    loadThread(active.id); loadList();
  };

  const act = async (cid, method, suffix = "") => {
    await fetch(`${API}/api/admin/chat/conversations/${cid}${suffix}`, { method, headers: h });
    if (method === "DELETE" && active?.id === cid) { setActive(null); setMessages([]); }
    else if (active?.id === cid) loadThread(cid);
    loadList();
  };

  const unread = convs.reduce((n, c) => n + (c.unread_admin || 0), 0);

  return (
    <div className="space-y-5" data-testid="admin-chat">
      <div className={`rounded-2xl border p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${online ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`} data-testid="admin-chat-status-card">
        <div className="flex items-center gap-3">
          <span className={`w-12 h-12 rounded-full flex items-center justify-center ${online ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-500"}`}>{online ? <Headset size={22} /> : <Bot size={22} />}</span>
          <div>
            <p className="font-display font-bold text-[#0B2545]" data-testid="admin-chat-status-label">{online ? "Du är online – besökare chattar med dig" : "Du är offline – AI-assistenten svarar"}</p>
            <p className="text-xs text-slate-500">{online ? "Svarar du inte inom 2 minuter tar AI-assistenten över tills du hoppar in." : "Slå på när du är på plats så ser besökarna att receptionen är tillgänglig."}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
        <button onClick={toggleSound} data-testid="admin-chat-toggle-sound" aria-pressed={sound} title={sound ? "Ljud på" : "Ljud av"} className={`w-11 h-11 rounded-xl flex items-center justify-center transition-colors ${sound ? "bg-[#f0f5fb] text-[#0B2545] hover:bg-[#e2ecf7]" : "bg-slate-100 text-slate-400 hover:bg-slate-200"}`}>{sound ? <Volume2 size={18} /> : <VolumeX size={18} />}</button>
        <button onClick={toggleOnline} data-testid="admin-chat-toggle-online" className={`inline-flex items-center gap-2 px-5 py-3 rounded-xl font-bold transition-colors ${online ? "bg-white border border-emerald-300 text-emerald-700 hover:bg-emerald-100" : "bg-emerald-500 text-white hover:bg-emerald-600"}`}>
          <Power size={17} /> {online ? "Gå offline" : "Gå online"}
        </button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[320px_1fr] gap-5 min-h-[520px]">
        <aside className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col" data-testid="admin-chat-list">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-bold text-[#0B2545] flex items-center justify-between">Samtal {unread > 0 && <span className="px-2 py-0.5 rounded-full bg-rose-500 text-white text-[10px]" data-testid="admin-chat-unread-total">{unread} nya</span>}</div>
          <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
            {convs.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">Inga chattar ännu.</p>}
            {convs.map((c) => (
              <button key={c.id} onClick={() => loadThread(c.id)} data-testid={`admin-chat-conv-${c.id}`} className={`w-full text-left px-4 py-3 hover:bg-[#f6f9fc] transition-colors ${active?.id === c.id ? "bg-[#f0f5fb]" : ""}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-semibold text-sm text-[#0B2545] truncate">{c.name}</span>
                  <span className="flex items-center gap-1.5">
                    {c.unread_admin > 0 && <span className="w-5 h-5 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center">{c.unread_admin}</span>}
                    <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.status === "closed" ? "bg-slate-100 text-slate-500" : c.mode === "ai" ? "bg-[#ffce3a]/30 text-[#7a5a00]" : "bg-emerald-100 text-emerald-700"}`}>{c.status === "closed" ? "Avslutad" : c.mode === "ai" ? "AI" : "Du"}</span>
                  </span>
                </div>
                <p className="text-xs text-slate-500 truncate mt-0.5">{c.last_text || "…"}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">{c.contact} · {new Date(c.updated_at).toLocaleString("sv-SE", { dateStyle: "short", timeStyle: "short" })}</p>
              </button>
            ))}
          </div>
        </aside>

        <section className="bg-white rounded-2xl border border-slate-200 overflow-hidden flex flex-col" data-testid="admin-chat-thread">
          {!active ? (
            <div className="flex-1 flex flex-col items-center justify-center text-slate-400 text-sm gap-2"><MessageSquare size={36} /> Välj ett samtal till vänster</div>
          ) : (
            <>
              <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-bold text-[#0B2545] truncate">{active.name} <span className="text-xs text-slate-400 font-normal">· {active.contact}</span></p>
                  <p className="text-xs text-slate-500">{active.status === "closed" ? "Avslutad" : active.mode === "ai" ? "AI-assistenten svarar just nu – skriv för att ta över" : "Du svarar i detta samtal"}</p>
                </div>
                <div className="flex gap-1.5">
                  {active.status === "open" && <button onClick={() => act(active.id, "POST", "/close")} data-testid="admin-chat-close-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#f0f5fb] text-[#0B2545] text-xs font-semibold hover:bg-[#e2ecf7]"><CheckCircle2 size={14} /> Avsluta</button>}
                  <button onClick={() => act(active.id, "DELETE")} data-testid="admin-chat-delete-btn" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-rose-600 text-xs font-semibold hover:bg-rose-50"><Trash2 size={14} /> Radera</button>
                </div>
              </div>
              <ChatMessages messages={messages} viewer="agent" />
              <form onSubmit={reply} className="p-3 border-t border-slate-100 flex items-center gap-2">
                <input data-testid="admin-chat-reply-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={active.status === "closed" ? "Skriv för att återöppna chatten…" : "Skriv ett svar…"} className="flex-1 px-4 py-2.5 rounded-xl bg-[#f6f9fc] border border-slate-200 focus:border-[#1B6CA8] outline-none text-sm" />
                <button data-testid="admin-chat-reply-send" disabled={!draft.trim()} className="w-11 h-11 rounded-xl bg-[#0B2545] text-white flex items-center justify-center hover:bg-[#13315C] disabled:opacity-40"><Send size={17} /></button>
              </form>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
